import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Wrench, Bell, Sparkles, Loader2, CheckCircle2, AlertCircle, RotateCcw, MessageSquare, Zap, Building2, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { settingsService } from '../services/settingsService';
import { organizationService } from '../services/organizationService';
import { billingService } from '../services/billingService';
import apiClient from '../services/authService';

// ── Group icon map ────────────────────────────────────────────────────────────
const GROUP_ICONS = { Wrench, Bell, Sparkles };

// ── Individual setting controls ───────────────────────────────────────────────

const NumericControl = ({ setting, value, onChange, disabled }) => (
  <div className="flex items-center gap-3">
    {setting.unit && (
      <span className="text-sm font-medium text-gray-500 w-5 text-right flex-shrink-0">{setting.unit}</span>
    )}
    <Input
      type="number"
      min={setting.min}
      max={setting.max}
      step={setting.step || 1}
      value={value ?? setting.default}
      onChange={e => onChange(setting.key, Number(e.target.value))}
      disabled={disabled}
      className="w-40 text-sm"
    />
    {setting.suffix && (
      <span className="text-xs text-muted-foreground">{setting.suffix}</span>
    )}
  </div>
);

const RangeControl = ({ setting, value, onChange, disabled }) => {
  const current = value ?? setting.default;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={setting.min}
          max={setting.max}
          step={setting.step || 1}
          value={current}
          onChange={e => onChange(setting.key, Number(e.target.value))}
          disabled={disabled}
          className="flex-1 accent-cyan-600"
        />
        <span className="text-sm font-semibold w-20 text-right text-gray-700">
          {setting.unit}{current.toLocaleString()}
        </span>
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground px-0.5">
        <span>{setting.unit}{setting.min?.toLocaleString()}</span>
        <span>{setting.unit}{setting.max?.toLocaleString()}</span>
      </div>
    </div>
  );
};

const BooleanControl = ({ setting, value, onChange, disabled }) => {
  const checked = value ?? setting.default;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(setting.key, !checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${
        checked ? 'bg-cyan-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
};

const StringControl = ({ setting, value, onChange, disabled }) => (
  <Input
    type="text"
    value={value ?? setting.default}
    onChange={e => onChange(setting.key, e.target.value)}
    placeholder={setting.placeholder || ''}
    maxLength={setting.maxLength}
    disabled={disabled}
    className="text-sm max-w-sm"
  />
);

const SelectControl = ({ setting, value, onChange, disabled }) => (
  <select
    value={value ?? setting.default}
    onChange={e => onChange(setting.key, e.target.value)}
    disabled={disabled}
    className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    {(setting.options || []).map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

const CONTROLS = {
  numeric: NumericControl,
  range:   RangeControl,
  boolean: BooleanControl,
  string:  StringControl,
  select:  SelectControl,
};

// ── Setting row ───────────────────────────────────────────────────────────────

const SettingRow = ({ setting, value, onChange, disabled }) => {
  const Control = CONTROLS[setting.type] || StringControl;
  const isBoolean = setting.type === 'boolean';

  return (
    <div className={`flex ${isBoolean ? 'items-center justify-between' : 'flex-col gap-2'} py-4 border-b last:border-0`}>
      <div className={isBoolean ? 'flex-1 pr-6' : ''}>
        <Label className="text-sm font-medium text-gray-800">{setting.label}</Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{setting.description}</p>
      </div>
      <div className={isBoolean ? 'flex-shrink-0' : ''}>
        <Control setting={setting} value={value} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
};

// ── Tenant Settings ───────────────────────────────────────────────────────────

const CHANNEL_OPTIONS = [
  { value: 'web',       label: 'Web (in-app)',  description: 'Receive updates through the Nivasapro web app.' },
  { value: 'email',     label: 'Email',         description: 'Get notifications sent to your email address.' },
  { value: 'sms',       label: 'SMS',           description: 'Receive text messages on your registered phone number.' },
  { value: 'whatsapp',  label: 'WhatsApp',      description: 'Get updates via WhatsApp on your registered number.' },
];

const TenantSettings = ({ user }) => {
  const [channel, setChannel]   = useState(user?.preferences?.preferredChannel || 'web');
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);
  const [isDirty, setIsDirty]   = useState(false);
  const original = user?.preferences?.preferredChannel || 'web';

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleChange = (value) => {
    setChannel(value);
    setIsDirty(value !== original);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/tenants/me', { preferredChannel: channel });
      // Persist into the in-memory user object so subsequent reads are correct
      if (user?.preferences) user.preferences.preferredChannel = channel;
      setIsDirty(false);
      showToast('success', 'Preferences saved.');
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">Manage your personal preferences.</p>
      </div>

      {toast && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm border ${
          toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {toast.type === 'success'
            ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
          {toast.msg}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="h-7 w-7 rounded-lg bg-cyan-50 flex items-center justify-center">
              <MessageSquare className="h-4 w-4 text-cyan-600" />
            </div>
            Communication Preferences
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Choose how you'd like to receive updates and notifications from your property manager.
          </p>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-2">
            {CHANNEL_OPTIONS.map(opt => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  channel === opt.value
                    ? 'border-cyan-500 bg-cyan-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="preferredChannel"
                  value={opt.value}
                  checked={channel === opt.value}
                  onChange={() => handleChange(opt.value)}
                  className="mt-0.5 accent-cyan-600"
                />
                <div>
                  <p className={`text-sm font-medium ${channel === opt.value ? 'text-cyan-700' : 'text-gray-800'}`}>
                    {opt.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                </div>
              </label>
            ))}
          </div>

          {isDirty && (
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setChannel(original); setIsDirty(false); }}
                disabled={saving}
              >
                Discard
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Save
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ── Plan Usage Card ───────────────────────────────────────────────────────────

const PLAN_LABELS = { free: 'Free', starter: 'Starter', professional: 'Professional', portfolio: 'Portfolio' };
const PLAN_COLORS = { free: 'text-gray-500', starter: 'text-cyan-600', professional: 'text-indigo-600', portfolio: 'text-amber-600' };
const PLAN_BADGE  = {
  free:         'bg-gray-100 text-gray-700 border-gray-200',
  starter:      'bg-cyan-100 text-cyan-800 border-cyan-200',
  professional: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  portfolio:    'bg-amber-100 text-amber-800 border-amber-200',
};
const PLAN_TAGLINE = {
  free:         'Basic access, great for getting started.',
  starter:      'WhatsApp, approvals & cost reporting included.',
  professional: 'SMS, advanced reporting & AI vendor selection.',
  portfolio:    'Unlimited everything. White-glove support.',
};

function UsageBar({ label, used, max, pct, atLimit }) {
  if (max === null) {
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{label}</span>
          <span className="text-green-600 font-medium">Unlimited</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-100" />
      </div>
    );
  }
  const color = atLimit ? 'bg-red-500' : pct >= 80 ? 'bg-amber-400' : 'bg-cyan-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span className={atLimit ? 'text-red-600 font-semibold' : ''}>
          {used.toLocaleString()} / {max.toLocaleString()}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

const PlanUsageCard = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    organizationService.getUsage()
      .then(setUsage)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleUpgrade = async (plan) => {
    setUpgrading(true);
    try { await billingService.startCheckout(plan); }
    catch { setUpgrading(false); }
  };

  const handleManage = async () => {
    setUpgrading(true);
    try { await billingService.openPortal(); }
    catch { setUpgrading(false); }
  };

  if (loading) return (
    <Card>
      <CardContent className="py-6 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </CardContent>
    </Card>
  );

  if (!usage) return null;

  const planLabel  = PLAN_LABELS[usage.plan]  ?? usage.plan;
  const planBadge  = PLAN_BADGE[usage.plan]   ?? PLAN_BADGE.free;
  const planTagline = PLAN_TAGLINE[usage.plan] ?? '';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          {/* Plan identity — left side */}
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Zap className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-base font-bold text-gray-900">Plan &amp; Usage</span>
                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${planBadge}`}>
                  {planLabel}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{planTagline}</p>
            </div>
          </div>
          {/* Action buttons — right side */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {usage.plan === 'free' && (
              <button
                onClick={() => handleUpgrade('starter')}
                disabled={upgrading}
                className="text-xs bg-cyan-600 text-white px-2.5 py-1 rounded-md font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50"
              >
                {upgrading ? 'Redirecting…' : 'Upgrade →'}
              </button>
            )}
            {usage.plan === 'starter' && (
              <button
                onClick={() => handleUpgrade('professional')}
                disabled={upgrading}
                className="text-xs bg-indigo-600 text-white px-2.5 py-1 rounded-md font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {upgrading ? 'Redirecting…' : 'Go Professional →'}
              </button>
            )}
            {(usage.plan === 'starter' || usage.plan === 'professional') && (
              <button
                onClick={handleManage}
                disabled={upgrading}
                className="text-xs text-gray-500 hover:text-gray-700 underline font-medium disabled:opacity-50"
              >
                Manage
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <UsageBar
            label="Properties"
            used={usage.usage.unitsUsed}
            max={usage.limits.maxUnits}
            pct={usage.warnings.unitsPct}
            atLimit={usage.warnings.unitsAtLimit}
          />
          <UsageBar
            label="Niva messages this month"
            used={usage.usage.messagesThisMonth}
            max={usage.limits.nivaMsgsPerMonth}
            pct={usage.warnings.messagesPct}
            atLimit={usage.warnings.messagesAtLimit}
          />
        </div>
        <div className="flex flex-wrap gap-3 pt-1">
          {[
            { label: 'WhatsApp', on: usage.limits.whatsappChannel },
            { label: 'SMS', on: usage.limits.smsChannel },
            { label: 'Owner approvals', on: usage.limits.ownerApprovals },
            { label: 'Cost reporting', on: usage.limits.costReporting },
          ].map(({ label, on }) => (
            <span
              key={label}
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
                on ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-gray-50 text-gray-400'
              }`}
            >
              {on ? '✓' : '✗'} {label}
            </span>
          ))}
        </div>
        {(usage.warnings.unitsAtLimit || usage.warnings.messagesAtLimit) && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              You've reached a plan limit.{' '}
              <a href="/features#pricing" className="font-semibold underline">Upgrade your plan</a>{' '}
              to continue growing.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Main Settings component (landlord/PM) ─────────────────────────────────────

const Settings = ({ user }) => {
  const location = useLocation();
  const [definition, setDefinition] = useState([]);
  const [values, setValues]         = useState({});
  const [dirty, setDirty]           = useState({});   // only changed keys
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [toast, setToast]           = useState(null); // { type: 'success'|'error', msg }

  // Show upgrade confirmation banner when returning from Stripe checkout
  useEffect(() => {
    if (new URLSearchParams(location.search).get('upgraded') === 'true') {
      showToast('success', 'Plan upgraded successfully! Your new limits are now active.');
      window.history.replaceState({}, '', '/settings');
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [def, vals] = await Promise.all([
        settingsService.getDefinition(),
        settingsService.get(),
      ]);
      setDefinition(def);
      setValues(vals);
      setDirty({});
    } catch (err) {
      console.error('Failed to load settings:', err);
      showToast('error', 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleChange = (key, value) => {
    setValues(v => ({ ...v, [key]: value }));
    setDirty(d => ({ ...d, [key]: value }));
  };

  const handleSave = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    try {
      const saved = await settingsService.save(dirty);
      setValues(saved);
      setDirty({});
      showToast('success', 'Settings saved.');
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDirty({});
    load();
  };

  const hasDirty = Object.keys(dirty).length > 0;

  // ── Tenant-specific settings ────────────────────────────────────────────────
  if (user?.userType === 'Tenant') {
    return <TenantSettings user={user} />;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure how Nivasapro behaves for your organisation.
          </p>
        </div>
        {hasDirty && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Discard
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Save changes
            </Button>
          </div>
        )}
      </div>

      {/* Plan usage */}
      <PlanUsageCard />

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm border ${
          toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {toast.type === 'success'
            ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
          {toast.msg}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className={`space-y-6 ${hasDirty ? 'pb-20' : ''}`}>
          {definition.map(group => {
            const Icon = GROUP_ICONS[group.icon] || Wrench;
            return (
              <Card key={group.key}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className="h-7 w-7 rounded-lg bg-cyan-50 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-cyan-600" />
                    </div>
                    {group.group}
                  </CardTitle>
                  {group.description && (
                    <p className="text-xs text-muted-foreground">{group.description}</p>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  {group.settings.map(setting => (
                    <SettingRow
                      key={setting.key}
                      setting={setting}
                      value={values[setting.key]}
                      onChange={handleChange}
                      disabled={saving}
                    />
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sticky save bar when dirty */}
      {hasDirty && !loading && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-white/95 backdrop-blur px-6 py-3 flex items-center justify-between shadow-lg">
          <p className="text-sm text-muted-foreground">
            You have unsaved changes.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
              Discard
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Save changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
